import { Dispatch } from "react";
import firebase from "../../firebase/firebase";
import { DB_COLLECTIONS } from "../../firebase/collections";
import { SignInData, SignUpData, User, UserSnippet } from "../../data/interfaces";
import { SIGN_IN_TYPES, SignInAction } from "./signIn/types";
import { SIGN_OUT_TYPES, SignOutAction } from "./signOut/types";
import { FUNCTIONS } from "../../firebase/functions";
import { History } from "history";
import { ROUTES } from "../../routing/routes";
import { COOKIES, USER_ROLES } from "../../data/enums";
import Cookies from "js-cookie";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../utils/analytics";

// -----------------------------------------------------------------
// U t i l s
// -----------------------------------------------------------------
/**
 * Check if user is not admin
 *
 * @function
 * @async
 *
 * @param authUser
 */
const isNotAdmin = async (authUser: firebase.User | null): Promise<any> => {
  if (!authUser) {
    return null;
  }

  // Load claims (roles)
  let tokenResult = await authUser.getIdTokenResult();

  return !tokenResult.claims.admin;
};

/**
 * Merges auth user data and firestore data to compose user obj.
 * @function
 * @async
 *
 * @param authUser
 * @param token
 */
const mergeAuthAndFirestoreUser = async (
  authUser: firebase.User | null,
  token?: string
): Promise<any> => {
  if (!authUser) {
    return null;
  }

  if (!token) {
    token = await authUser.getIdToken();
  }

  // Get user from firestore
  console.log("[Firebase] read");
  let firestoreUser = await firebase
    .firestore()
    .collection(DB_COLLECTIONS.USERS)
    .doc(authUser.uid)
    .get();

  let firestoreData: any = firestoreUser.data();

  // Merge firebase Auth User with Firestore Data
  let user: any = {
    ...{
      email: authUser.email,
      uid: authUser.uid,
      email_verified: true, //authUser.emailVerified,
      token: token,
    },
    ...firestoreData,
  };

  return user;
};

// -----------------------------------------------------------------
// A c t i o n s
// -----------------------------------------------------------------
/**
 * Handles user presence (online, offline status)
 * @function
 * @async
 */
export const handleUserPresence = async () => {
  return;
  // try {
  //     console.log("handleUserPresence");

  //     const firestoreDb = firebase.firestore();
  //     const realTimeDb = firebase.database();

  //     // Get current firebase user
  //     const uid = firebase.auth().currentUser?.uid;

  //     if (!uid) {
  //         return;
  //     }

  //     // Get a reference to the Users collection
  //     const usersRef = firestoreDb.collection(DB_COLLECTIONS.USERS);

  //     // Get a reference to the list of connections
  //     const onlineRef = realTimeDb.ref(".info/connected");

  //     onlineRef.on("value", (snapshot) => {
  //         realTimeDb
  //             .ref(`/status/${uid}`)
  //             .onDisconnect() // Set up the disconnect hook
  //             .set("offline") // The value to be set for this key when the client disconnects
  //             .then(() => {
  //                 // Set the Firestore User's online status to true
  //                 usersRef.doc(uid).set(
  //                     {
  //                         online: true,
  //                     },
  //                     { merge: true }
  //                 );

  //                 // Let's also create a key in our real-time database
  //                 // The value is set to 'online'
  //                 realTimeDb.ref(`/status/${uid}`).set("online");
  //             });
  //     });
  // } catch (err) {
  //     console.error(err);
  // }
};

/**
 * Mark user as offline
 * @function
 *
 * @param uid
 */
export const setUserOffline = (uid?: string) => {
  return;
  // try {
  //     console.log("setUserOffline");
  //     if (!uid) {
  //         return;
  //     }

  //     // Set offline in firestore
  //     firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(uid).update({
  //         online: false,
  //     });

  //     // Set offline in realtime db
  //     firebase.database().ref(`/status/${uid}`).set("offline");
  // } catch (err) {
  //     console.error(err);
  // }
};

/**
 * Sign in method through SignInData interface: once completed,
 * set token in local storage and update user object in redux state
 * @function
 * @async
 *
 * @param data
 */
export const signIn = (data: SignInData) => async (dispatch: Dispatch<SignInAction>) => {
  try {
    firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);

    dispatch({ type: SIGN_IN_TYPES.PENDING });
    const { email, password } = data;
    const response = await firebase.auth().signInWithEmailAndPassword(email, password);

    let { user } = response;

    const token = await user?.getIdToken(true);

    const canContinue = await isNotAdmin(user);
    if (!canContinue) {
      firebase.auth().signOut();
      dispatch({
        type: SIGN_IN_TYPES.ADD_ERROR,
        payload: "Invalid credentials",
      });
      return;
    }

    let userData = await mergeAuthAndFirestoreUser(user, token);

    if (!userData.email_verified) {
      // Logout until email confirmation
      firebase.auth().signOut();

      dispatch({
        type: SIGN_IN_TYPES.ADD_ERROR,
        payload: "EMAIL_NOT_VERIFIED",
      });
      return;
    } else {
      if (token && user && user.uid && user.email) {
        // localStorage.setItem(TOKEN, token);
        handleUserPresence();

        // Track event
        trackAnalyticsEvent(ANALYTICS_EVENTS.SIGN_IN, { email: user.email });

        dispatch({
          type: SIGN_IN_TYPES.COMPLETED,
          payload: userData,
        });
      }
    }
  } catch (err) {
    dispatch({
      type: SIGN_IN_TYPES.ADD_ERROR,
      payload: err.message,
    });
  }
};

/**
 *  Social sign In with Google Provider
 * @function
 * @async
 */
export const signInWithGoogle = () => async (dispatch: Dispatch<any>) => {
  return;
  // try {
  //     firebase.auth().setPersistence(firebase.auth.Auth.Persistence.NONE);

  //     // Load google provider
  //     const provider = new firebase.auth.GoogleAuthProvider();

  //     // To use lang of the current device
  //     firebase.auth().useDeviceLanguage();

  //     const response = await firebase.auth().signInWithPopup(provider);

  //     const uid = response.user?.uid;
  //     const token = (response.credential as any)?.accessToken;

  //     const isNewUser = response.additionalUserInfo?.isNewUser;

  //     if (!!isNewUser) {
  //         let user = {
  //             firstname: (response.additionalUserInfo?.profile as any).given_name,
  //             lastname: (response.additionalUserInfo?.profile as any).family_name,
  //             email: (response.additionalUserInfo?.profile as any).email,
  //             email_verified: true,
  //             profile_image_url: (response.additionalUserInfo?.profile as any).picture,
  //             member_since: firebase.firestore.Timestamp.now(),
  //         };

  //         // Create user in firestore
  //         console.log("[Firebase] write");
  //         await firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(uid).set(user);
  //     }

  //     const userData = await mergeAuthAndFirestoreUser(firebase.auth().currentUser, token);

  //     handleUserPresence();
  //     dispatch({
  //         type: SIGN_IN_TYPES.COMPLETED,
  //         payload: userData,
  //     });
  // } catch (error) {
  //     dispatch({
  //         type: SIGN_IN_TYPES.ADD_ERROR,
  //         payload: error.message,
  //     });
  // }
};

/**
 *  Social sign In with LinkedIn Provider
 * @function
 * @async
 *
 * @param data
 */
export const signInWithLinkedIn = (data: any) => async (dispatch: Dispatch<any>) => {
  return;
  // try {
  //     console.log("[signInWithLinkedIn]", data);

  //     // User already created on auth, must create it on firestore if not exists
  //     if (!!data.isNewUser) {
  //         let user = {
  //             firstname: data.userData.firstname,
  //             lastname: data.userData.lastname,
  //             email: data.userData.email,
  //             email_verified: true,
  //             profile_image_url: data.userData.picture,
  //             member_since: firebase.firestore.Timestamp.now(),
  //         };

  //         // Create user in firestore
  //         console.log("[Firebase] write");
  //         await firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(data.uid).set(user);
  //     }

  //     await firebase.auth().signInWithCustomToken(data.token);

  //     const userData = await mergeAuthAndFirestoreUser(firebase.auth().currentUser, data.token);

  //     console.log(userData);

  //     handleUserPresence();
  //     dispatch({
  //         type: SIGN_IN_TYPES.COMPLETED,
  //         payload: userData,
  //     });
  // } catch (error) {
  //     dispatch({
  //         type: SIGN_IN_TYPES.ADD_ERROR,
  //         payload: error.message,
  //     });
  // }
};

/**
 * Sign up method through SignUpData interface: once completed,
 * set token in local storage and update user object in redux state
 * @function
 * @async
 *
 * @param data
 */
export const signUp = async (data: SignUpData) => {
  try {
    firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);

    const { email, password } = data;
    const response = await firebase.auth().createUserWithEmailAndPassword(email, password);

    let { user } = response;

    const uid = user?.uid;
    // const token = await user?.getIdToken(true);

    // Store password on firestore temporary "secure" collection
    await firebase.firestore().collection(DB_COLLECTIONS.USERS_PRIVATE).doc(uid).set({
      password: data.password,
    });

    // Create user in firestore
    console.log("[Firebase] write user");
    await firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(uid).set({
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      privacy_policy_consent: data.privacy_policy_consent,
      role: USER_ROLES.VISITOR,
      member_since: firebase.firestore.Timestamp.now(),
    });

    // Track event
    trackAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP, { email: data.email });

    // Logout until email confirmation
    firebase.auth().signOut();

    return true;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Complete profile method , create user detail and
 * set user profile_completed = true
 * @function
 * @async
 *
 * @param user
 * @param data
 */
export const completeUserProfile = async (user: User | undefined | null, data: any) => {
  try {
    if (!user) {
      console.warn("User is not defined");
      return;
    }

    console.log("[Firebase] write");

    // Create user detail on firestore
    console.log("[Firebase] write user detail");
    await firebase
      .firestore()
      .collection(DB_COLLECTIONS.USERS_DETAILS)
      .doc(user.uid)
      .set(
        {
          profile_completed: true,
          phone_number: data.phone_number ? data.phone_number : null,
          company: data.company,
          cfu_info: data.cfu_info ? data.cfu_info : null,
          international_buyer: !!data.international_buyer,
          prev_edition: !!data.prev_edition,
        },
        { merge: true }
      );

    // Here, a cloud function will be triggered automatically and will update data on user main collection

    handleUserPresence();

    return {
      ...user,
      ...{
        international_buyer: !!data.international_buyer,
        company: data.company.name,
        company_role: data.company.user_role,
        profile_completed: true,
        phone_number: data.phone_number,
      },
    };
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Sign out method: once completed, remove token in local storage and update user object in redux state
 * @function
 * @async
 *
 * @param history
 */
export const signOut = (history?: History) => async (dispatch: Dispatch<SignOutAction>) => {
  try {
    dispatch({ type: SIGN_OUT_TYPES.PENDING });

    Cookies.remove(COOKIES.INTRO_VIDEO, { path: "" });

    setUserOffline(firebase.auth().currentUser?.uid);

    // Track event
    trackAnalyticsEvent(ANALYTICS_EVENTS.SIGN_OUT);

    await firebase.auth().signOut();

    // replace state
    if (!!history) {
      history.push(ROUTES.SIGN_IN, { from: undefined });
    }

    dispatch({
      type: SIGN_OUT_TYPES.COMPLETED,
    });
  } catch (err) {
    dispatch({
      type: SIGN_OUT_TYPES.ADD_ERROR,
      payload: err,
    });
  }
};

/**
 * Clear signIn error message from redux store.
 * @function
 * @async
 */
export const cleanSignInErrorMessage = () => async (dispatch: Dispatch<SignInAction>) => {
  try {
    dispatch({ type: SIGN_IN_TYPES.CLEAR_ERROR });
  } catch (err) {
    console.error(`[AuthAction]: ${err}`);
  }
};

/**
 * Sends a user confirm email
 * @function
 * @async
 *
 * @param email
 */
export const sendConfirmationEmail = async (email: string) => {
  if (!email) {
    return;
  }

  try {
    const func = firebase.functions().httpsCallable(FUNCTIONS.SEND_CONFIRMATION_EMAIL);

    const result = await func(email);

    return result.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Sends a user reset password via email
 * @function
 * @async
 *
 * @param email
 */
export const sendResetPasswordEmail = async (email: string) => {
  if (!email) {
    return;
  }

  // Track event
  trackAnalyticsEvent(ANALYTICS_EVENTS.FORGOT_PASSWORD, { email: email });

  try {
    const func = firebase.functions().httpsCallable(FUNCTIONS.SEND_RESET_PASSWORD_EMAIL);

    const result = await func(email);

    return result.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Deletes user's profile via callable cloud function
 * @function
 * @async
 *
 * @param uid
 */
export const deleteUserProfile = async (uid?: string) => {
  if (!uid) {
    return;
  }

  // Track event
  trackAnalyticsEvent(ANALYTICS_EVENTS.DELETE_USER);

  try {
    const deleteUser = firebase.functions().httpsCallable(FUNCTIONS.DELETE_USER);

    const result = await deleteUser();

    return result.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Creates user on mattermost chat
 * @function
 * @async
 *
 * @param user
 */
export const createMattermostUser = async (user?: UserSnippet | null) => {
  if (!user) {
    return;
  }

  // Track event
  trackAnalyticsEvent(ANALYTICS_EVENTS.CREATE_MATTERMOST_USER, { email: user.email });

  try {
    const func = firebase.functions().httpsCallable(FUNCTIONS.CREATE_MATTERMOST_USER);

    const result = await func({
      uid: user.uid,
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
      company: user.company,
      role: user.company_role,
    });

    return result.data.mattermost;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Update password on users_private
 * @function
 * @async
 *
 * @param email
 * @param password
 */
export const updatePassword = async (email?: string, password?: string) => {
  if (!email || !password) {
    return;
  }

  try {
    const func = firebase.functions().httpsCallable(FUNCTIONS.UPDATE_PASSWORD);

    const result = await func({ email, password });

    return result;
  } catch (err) {
    console.error(err);
    throw err;
  }
};
